import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "@aws-amplify/ui-react";
import {
	AwsCredentialProvider,
	FaceLivenessDetectorCore,
} from "@aws-amplify/ui-react-liveness";
import "@aws-amplify/ui-react/styles.css";
import { ButtonBack } from "../../../../../core/components/CustomButton";
import { TextComponent } from "../../../../../core/components/TextComponent";
import CircularProgress from "../../../../../core/components/CircularProgress";
import { ModalSimple } from "../../../../../core/components/Modal";
import { CheckVerifyBG } from "../../../../../core/icons/CheckVerifyBG";
import { useAlert } from "../../../../../core/hooks/useAlert";
import { AppDispatch } from "../../../../../core/store/store";
import { SignupPhotoLivenessCheckResult } from "../../../domain/entities/signupPhotoLiveness";
import {
	onSaveSignupPhotoLivenessResult,
} from "../../store/OnboardingThunk";
import { setUserInfo } from "../../store/OnboardingSlice";

const SIGNUP_PHOTO_LIVENESS_SESSION_KEY = "signup_photo_liveness_pending";

type OnboardingLivenessLocationState = {
	result?: SignupPhotoLivenessCheckResult;
};

const readStoredResult = (): SignupPhotoLivenessCheckResult | undefined => {
	if (typeof window === "undefined") {
		return undefined;
	}

	const rawValue = window.sessionStorage.getItem(
		SIGNUP_PHOTO_LIVENESS_SESSION_KEY
	);
	if (!rawValue) {
		return undefined;
	}

	try {
		return JSON.parse(rawValue) as SignupPhotoLivenessCheckResult;
	} catch (_) {
		window.sessionStorage.removeItem(SIGNUP_PHOTO_LIVENESS_SESSION_KEY);
		return undefined;
	}
};

const clearStoredResult = () => {
	if (typeof window === "undefined") {
		return;
	}
	window.sessionStorage.removeItem(SIGNUP_PHOTO_LIVENESS_SESSION_KEY);
};

export const OnboardingLivenessScreen = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch: AppDispatch = useDispatch();
	const { showAlert } = useAlert();
	const [isSubmittedModalOpen, setIsSubmittedModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const result =
		(location.state as OnboardingLivenessLocationState | null)?.result ??
		readStoredResult();
	const session = result?.livenessSession ?? undefined;

	const credentialProvider = useMemo<AwsCredentialProvider | undefined>(() => {
		if (!session) {
			return undefined;
		}

		return async () => ({
			accessKeyId: session.accessKeyId,
			secretAccessKey: session.secretAccessKey,
			sessionToken: session.sessionToken,
			expiration: session.expiresAt ? new Date(session.expiresAt) : undefined,
		});
	}, [session]);

	const getSaveMessage = (status: string) => {
		switch (status) {
			case "failed":
				return "Live check could not confirm your face. Please try again.";
			case "expired":
				return "Live check expired. Please try again.";
			case "pending_result":
				return "Live check is still being checked. Please wait a moment and try again.";
			default:
				return "Live check could not be saved. Please try again.";
		}
	};

	const handleAnalysisComplete = async () => {
		if (!session?.sessionId || !result?.contextToken) {
			showAlert({
				type: "error",
				message: "Live check details are missing. Please go back and try again.",
			});
			return;
		}

		setIsSaving(true);
		const action = await dispatch(
			onSaveSignupPhotoLivenessResult({
				sessionId: session.sessionId,
				contextToken: result.contextToken,
			})
		);
		setIsSaving(false);

		if (onSaveSignupPhotoLivenessResult.fulfilled.match(action)) {
			if (action.payload.status === "passed") {
				if (!action.payload.approvalToken) {
					showAlert({
						type: "error",
						message: "Live check passed, but the approval token is missing.",
					});
					return;
				}

				dispatch(
					setUserInfo({
						signup_photo_liveness_token: action.payload.approvalToken,
					})
				);
				clearStoredResult();
				setIsSubmittedModalOpen(true);
				return;
			}

			showAlert({
				type: action.payload.status === "pending_result" ? "info" : "error",
				message: getSaveMessage(action.payload.status),
			});
			return;
		}

		showAlert({
			type: "error",
			message: "Could not save the live check. Please try again.",
		});
	};

	const handleError = (error: any) => {
		showAlert({
			type: "error",
			message:
				error?.state === "CAMERA_ACCESS_ERROR"
					? "Camera access is required for the live check."
					: "Live check could not be completed. Please try again.",
		});
	};

	if (!session || !credentialProvider) {
		return (
			<div className="bg-white p-8">
				<ButtonBack
					onClick={() => {
						clearStoredResult();
						navigate("/profilepicture");
					}}
				>
					Go Back
				</ButtonBack>
				<div className="mt-8">
					<TextComponent variant="h3" className="font-bold">
						Live check
					</TextComponent>
					<TextComponent variant="h6" className="mt-3">
						Live check details are missing. Please go back and try again.
					</TextComponent>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="bg-white p-8 max-md:overflow-y-auto">
				<ButtonBack
					onClick={() => {
						clearStoredResult();
						navigate("/profilepicture");
					}}
				>
					Go Back
				</ButtonBack>
				<div className="mt-8 mb-6">
					<TextComponent variant="h3" className="font-bold">
						Live check
					</TextComponent>
					<TextComponent variant="h6" className="mt-3 md:w-3/4">
						One extra live check is needed before you can continue with onboarding.
					</TextComponent>
				</div>
				<div className="rounded-3xl overflow-hidden bg-grayDefault min-h-[640px]">
					{isSaving ? (
						<div className="flex min-h-[640px] items-center justify-center">
							<CircularProgress />
						</div>
					) : (
						<ThemeProvider>
							<FaceLivenessDetectorCore
								sessionId={session.sessionId}
								region={session.region}
								onAnalysisComplete={handleAnalysisComplete}
								onError={handleError}
								onUserCancel={() => {
									clearStoredResult();
									navigate("/profilepicture");
								}}
								config={{ credentialProvider }}
							/>
						</ThemeProvider>
					)}
				</div>
			</div>
			<ModalSimple
				title="Information"
				variant="secondary"
				actionExecuted={isSubmittedModalOpen}
				onClose={() => {
					setIsSubmittedModalOpen(false);
					navigate("/addimage");
				}}
				icon={<CheckVerifyBG />}
				next
			>
				<TextComponent variant="h5">
					Thanks for submitting your photo. You can continue with the rest of onboarding now.
				</TextComponent>
			</ModalSimple>
		</>
	);
};
